import * as React from "react";
import { CommonHelper } from "../../../../../../../react-core/Helpers/CommonHelper";
import { Accordion } from "../../../../../../../react-core/Html/Accordion";
import { CircleBtn, CircleBtnType } from "../../../../../../../react-core/Html/CircleBtn";
import { SelectEditor } from "../../../../../../../react-core/Html/SelectEditor";
import { BankAccount } from "../../../../../../../Wrap/Models/Domains/BankAccount/BankAccount";
import { BankAccount as BankAccountComponent } from "./BankAccount";
import { BankAccountInstruction } from "../../../../../../../Wrap/Models/Domains/Instructions/BankAccountInstruction";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { EWizardLinterStatus, WizardLinter } from "../../../../../../Shared/Process/Process/WizardLinter";
import { EWizardLinterHelper } from "../../../NewBusinessWizard/Resource/EWizardLinterHelper";
import { BankAccountOwnerPaymentTypeMap, PaymentType } from "../../Resource/Contents";

import { NewBankAccountInstruction } from "../BankAccount/NewBankAccountInstruction";
import { ContentsTable } from "../../../NewBusinessWizard/Resource/Contents";
import { WithdrawalMethod } from "../../../../../../../Wrap/Constants/Enums/Domain/WithdrawalMethod";

interface ISelectBankAccountProps extends IProcessComponentProps {
    bankAccounts: BankAccount[];
    bankAccountInstructions: BankAccountInstruction[];
    payeeType: PaymentType;
    onSaveNewBankAccountInstruction?: (BankAccountInstruction: BankAccountInstruction) => void;
    onRemoveBankAccountInstruction?: (BankAccountInstruction: BankAccountInstruction) => void;
    inUseBankAccountInstruction?: string[];
    customerId: number;
    autoLineCompleted?: boolean;
}

interface ISelectBankAccountState {
    addNewBankAccount: boolean;
}

export class SelectBankAccount extends ProcessComponent<ISelectBankAccountProps, ISelectBankAccountState> {
    public static defaultProps = {
        autoLineCompleted: true,
    };
    constructor(props: any) {
        super(props);
        this.state = { addNewBankAccount: false };
    }

    getBlockStatus(): EWizardLinterStatus {
        const { data } = this.props;
        const complete = data != null && data.BankAccountNumber != null && data.BankAccountNumber != "";
        return EWizardLinterHelper.getEWizardLinterStatus(complete);
    }

    public render() {
        const bankAccountsInfo = this.props.bankAccounts || [];
        const bankAccountInstructions = this.props.bankAccountInstructions || [];
        const { payeeType, data, onChange } = this.props;

        const accountsFromExisting =
            bankAccountsInfo
                .filter((x) => (BankAccountOwnerPaymentTypeMap[x.Owner] || []).indexOf(payeeType) != -1)
                .map((account) => account.AccountNumber) || [];
        const bankAccountInstructionsFiltered = bankAccountInstructions.filter(
            (x) => (BankAccountOwnerPaymentTypeMap[x.Owner] || []).indexOf(payeeType) != -1
        );
        const accountsFromInstructions = bankAccountInstructionsFiltered.map((account) => account.AccountNumber) || [];
        const allbankAccounts = [...accountsFromExisting, ...accountsFromInstructions];
        const bankAccounts = Array.from(new Set(allbankAccounts));
        const bankAccount = bankAccountsInfo.find((account) => account.AccountNumber === data.BankAccountNumber);
        const bankNum = bankAccountsInfo.length + bankAccountInstructions.length;

        return (
            <WizardLinter status={this.getBlockStatus()}>
                {this.renderBankAccount(data, onChange, bankAccounts, bankAccount)}
                {this.renderBankAccountInstructions(payeeType, bankAccountInstructionsFiltered, bankNum)}
            </WizardLinter>
        );
    }

    private renderBankAccount = (data: any, onChange, bankAccounts: any, bankAccount: BankAccount) => {
        return (
            <>
                <div className="row">
                    <div className="col-md-4 col-12">
                        <SelectEditor
                            options={bankAccounts}
                            getText={(v) => v}
                            required={"Please select or add a bank account"}
                            placeholder="No account selected"
                            selected={data.BankAccountNumber}
                            label="Bank account"
                            onChange={(v) => onChange({ BankAccountNumber: v })}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-4 col-12">
                        <div>
                            {!CommonHelper.isNullOrEmpty(bankAccount) && <BankAccountComponent data={bankAccount} />}
                        </div>
                    </div>
                </div>
                {!CommonHelper.isNullOrEmpty(bankAccount) && bankAccount.DDIReceived && (
                    <div>
                        <p>{ContentsTable.DirectDebitMethodInformation4}</p>
                    </div>
                )}
            </>
        );
    };

    private renderBankAccountInstructions(
        payeeType: PaymentType,
        bankAccountInstructions: BankAccountInstruction[],
        currentBankAccountNum: number
    ) {
        const maxBankNum = 7;
        const showAddButton = currentBankAccountNum < maxBankNum && !this.state.addNewBankAccount;
        const { customerId } = this.props;
        return (
            <>
                {showAddButton && (
                    <CircleBtn
                        type={CircleBtnType.add}
                        title="Add new bank account"
                        onClick={() => this.setState({ addNewBankAccount: true })}
                        disabled={!this.props.autoLineCompleted}
                    />
                )}
                
                {bankAccountInstructions.map((bankAccountInstruction) => (
                    <>
                        <Accordion
                            key={`Bank account`}
                            label={`Bank account`}
                            cssWrapper="accordion-simple bigger-arrow"
                            isOpen={true}
                        >
                            <NewBankAccountInstruction
                                onChange={() => {}}
                                data={bankAccountInstruction}
                                onRef={(ref) => this.appendChild(ref)}
                                payeeType={payeeType}
                                readonly={true}
                                customerId={customerId}
                            />

                            {!(this.props.inUseBankAccountInstruction || []).some(
                                (item) => item == bankAccountInstruction.AccountNumber
                            ) && (
                                <CircleBtn
                                    type={CircleBtnType.remove}
                                    title="Remove this bank account"
                                    onClick={() => this.onRemoveBankAccountInstruction(bankAccountInstruction)}
                                    disabled={!this.props.autoLineCompleted}
                                />
                            )}
                        </Accordion>
                    </>
                ))}
                {this.state.addNewBankAccount && this.renderNewBankAccountInstruction(payeeType)}
            </>
        );
    }

    private renderNewBankAccountInstruction(payeeType: PaymentType) {
        const { customerId } = this.props;
        return (
            <>
                <CircleBtn
                    cssWrapper="mb-20"
                    type={CircleBtnType.remove}
                    title="Cancel add bank account"
                    onClick={() => this.setState({ addNewBankAccount: false })}
                    disabled={!this.props.autoLineCompleted}
                />
                <NewBankAccountInstruction
                    onChange={(instruction) => {
                        this.onSaveNewBankAccountInstruction(instruction);
                    }}
                    data={null}
                    onRef={(ref) => this.appendChild(ref)}
                    payeeType={payeeType}
                    isReview={false}
                    customerId={customerId}
                />
            </>
        );
    }

    private onRemoveBankAccountInstruction(bankAccountInstruction: BankAccountInstruction) {
        const { bankAccountInstructions, data, onChange } = this.props;
        const index = bankAccountInstructions.findIndex((x) => x.AccountNumber == bankAccountInstruction.AccountNumber);
        if (index != -1) {
            this.props.onRemoveBankAccountInstruction(bankAccountInstruction);
        }
    }

    private onSaveNewBankAccountInstruction(bankAccountInstruction: BankAccountInstruction) {
        const { bankAccountInstructions, onSaveNewBankAccountInstruction } = this.props;
        if (bankAccountInstructions.findIndex((x) => x.AccountNumber == bankAccountInstruction.AccountNumber) == -1) {
            onSaveNewBankAccountInstruction(bankAccountInstruction);
            this.setState({ addNewBankAccount: false });
        } else {
            return this.props.showErrorMsgPopUp(["Please enter a non-existing bank account"], true);
        }
    }
}
